import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { PeriodTimelineBlocksLoading } from '~/modules/resourcing/common/chart/components';
import {
  useGetSmartFitResourceAvailablityHoursOptions,
  useGetSmartFitResourceAvailablityHoursOptionsByTotalHours
} from '~/modules/resourcing/hooks';
import { useMeContext } from '~/modules/me';
import ResourceAssignmentSmartFitConfirmationDialog from '../ResourceAssignmentSmartFitConfirmationDialog';
import getShowSmartMatchDialog from './useShowSmartMatchDialog';

import useStyles from './useStyles';

const ShowSmartFitDialogOrLoading = ({
  classes: classesOverrides,
  resourceUser,
  scheduleRules,
  scale,
  chartStartDate,
  createAllocationWithUserAndOverrideRules,
  closeShowSmartFitDialogOrLoading,
  projectDefaultScheduleRule,
  // new query related props
  useNewSmartFitQuery = false,
  totalAllocatedHours,
  allocationDateRange
}) => {
  const classes = useStyles({ classes: classesOverrides });
  const me = useMeContext();

  const smartFitHookOld = useGetSmartFitResourceAvailablityHoursOptions({
    userUri: resourceUser.id,
    scale,
    scheduleRules: (scheduleRules || []).map(r => ({
      dateRange: {
        startDate: r.dateRange.startDate,
        endDate: r.dateRange.endDate
      },
      do: r.do
    })),
    skip: useNewSmartFitQuery
  });

  const smartFitHookNew = useGetSmartFitResourceAvailablityHoursOptionsByTotalHours(
    {
      userUri: resourceUser.id,
      dateRange: allocationDateRange,
      hoursPerPeriod: totalAllocatedHours,
      skip: !useNewSmartFitQuery
    }
  );

  const { isLoading, smartFitmentDetails } = useNewSmartFitQuery
    ? smartFitHookNew
    : smartFitHookOld;

  const { availableScheduleRulesWhatFits = [] } = smartFitmentDetails || {};

  const showSmartMatchDialog =
    !isLoading &&
    getShowSmartMatchDialog({
      me,
      smartFitmentDetails,
      scale,
      scheduleRules,
      totalAllocatedHours,
      useNewSmartFitQuery
    });

  useEffect(() => {
    if (!isLoading && !showSmartMatchDialog)
      createAllocationWithUserAndOverrideRules(
        useNewSmartFitQuery
          ? availableScheduleRulesWhatFits
          : availableScheduleRulesWhatFits || scheduleRules
      );
    // prevent running this effect before the smart fitment details are loaded, hence the dependency array eslint-disable comment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createAllocationWithUserAndOverrideRules,
    isLoading,
    showSmartMatchDialog
  ]);

  if (isLoading)
    return (
      <PeriodTimelineBlocksLoading
        classes={classes}
        scale={scale}
        chartStartDate={chartStartDate}
        scheduleRules={scheduleRules}
        projectDefaultScheduleRule={projectDefaultScheduleRule}
      />
    );

  return showSmartMatchDialog ? (
    <ResourceAssignmentSmartFitConfirmationDialog
      smartFitmentDetails={smartFitmentDetails}
      scale={scale}
      scheduleRules={useNewSmartFitQuery ? [] : scheduleRules}
      resourceUser={resourceUser}
      createAllocationWithUserAndOverrideRules={
        createAllocationWithUserAndOverrideRules
      }
      closeShowSmartFitDialogOrLoading={closeShowSmartFitDialogOrLoading}
    />
  ) : null;
};

ShowSmartFitDialogOrLoading.propTypes = {
  allocationDateRange: PropTypes.object,
  chartStartDate: PropTypes.object,
  classes: PropTypes.object,
  closeShowSmartFitDialogOrLoading: PropTypes.func,
  createAllocationWithUserAndOverrideRules: PropTypes.func,
  projectDefaultScheduleRule: PropTypes.object,
  resourceUser: PropTypes.object,
  scale: PropTypes.string,
  scheduleRules: PropTypes.array,
  totalAllocatedHours: PropTypes.number,
  useNewSmartFitQuery: PropTypes.bool
};

export default ShowSmartFitDialogOrLoading;
